import { memo, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import Autocomplete from "components/DesignComponents/Autocomplete";
import Checkbox from "components/DesignComponents/Checkbox";
import HighlightedOption from "components/HighlightedOption";
import { InfoTooltip } from "components/Table/components/InfoTooltip";
import {
    body2SemiBoldStyles,
    body3RegularStyles
} from "shared/styles/typography";
import {
    CONFIGURATION_FEATURE_SETTINGS_MODES,
    DROPDOWN_SIZES
} from "shared/types/enums";

import { LABELS, MODE_OPTIONS, TRANSITION_DURATION } from "./constants";
import { SingleFeatureProps } from "./types";

const SingleFeature = memo(
    ({ fileType, value, onChange }: SingleFeatureProps) => {
        const { gray700 } = colors;

        const { t } = useTranslation();

        const [modeValue, setModeValue] = useState("");

        const label = LABELS[fileType];
        const isChecked = value !== null;

        return (
            <div>
                <Checkbox
                    label={
                        <span
                            css={css({
                                display: "flex",
                                alignItems: "center"
                            })}
                        >
                            {t(`General##${label}`)}

                            <InfoTooltip
                                title={t(
                                    `Dialog##auto sync toggle##tooltips##${fileType}`
                                )}
                                css={css({
                                    marginLeft: "8px"
                                })}
                            />
                        </span>
                    }
                    checked={isChecked}
                    onChange={(_, isChecked) =>
                        onChange(
                            fileType,
                            isChecked
                                ? CONFIGURATION_FEATURE_SETTINGS_MODES.Enabled
                                : null
                        )
                    }
                />

                <div
                    css={css({
                        overflow: "hidden",
                        maxHeight: isChecked ? "52px" : "0px",
                        opacity: isChecked ? 1 : 0,
                        transition: `max-height ${TRANSITION_DURATION}s ease, opacity ${TRANSITION_DURATION}s ease`
                    })}
                >
                    <Autocomplete
                        disableClearable
                        size={DROPDOWN_SIZES.Medium}
                        options={MODE_OPTIONS}
                        value={value}
                        getOptionLabel={option =>
                            t(`Dialog##auto sync toggle##modes##${option}`)
                        }
                        renderOption={(propsData, modeName) => {
                            const { key: _key, ...restPropsData } = propsData;

                            return (
                                <li key={modeName}>
                                    <span
                                        {...restPropsData}
                                        css={css({
                                            flexDirection: "column",

                                            "&.MuiAutocomplete-option": {
                                                alignItems: "flex-start"
                                            }
                                        })}
                                    >
                                        <span css={body2SemiBoldStyles}>
                                            <HighlightedOption
                                                text={t(
                                                    `Dialog##auto sync toggle##modes##${modeName}`
                                                )}
                                                query={modeValue}
                                            />
                                        </span>

                                        <span
                                            css={[
                                                body3RegularStyles,
                                                { color: gray700 }
                                            ]}
                                        >
                                            {t(
                                                `Dialog##auto sync toggle##descriptions##${modeName}`
                                            )}
                                        </span>
                                    </span>
                                </li>
                            );
                        }}
                        onChange={(_, value) => onChange(fileType, value)}
                        onInputChange={(_, newInputValue, reason) =>
                            reason === "input" && setModeValue(newInputValue)
                        }
                        onClose={() => setModeValue("")}
                        customStyle={{
                            margin: "8px 0 0 32px"
                        }}
                    />
                </div>
            </div>
        );
    }
);

export default SingleFeature;
